import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "../../utils/Api";
import Translate from "../../utils/Translate";
import { formatDate } from "../../utils/dateTimeFormat";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import Page from "../../components/Page";
import { CheckCircleOutline } from "@mui/icons-material";
import BankIdAuthentication from "../../components/bankId/BankIdAuthentication";
import { useSnackbar } from "notistack";
import Preloader from "../../components/Preloader";

const RootStyle = styled(Page)(({ theme }) => ({
  display: "flex",
}));

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 600,
  margin: "auto",
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  justifyContent: "center",
  alignContent: "center",
  //padding: theme.spacing(12, 0),
}));

function getSignStartUrl() {
  return `${process.env.REACT_APP_MAIN_URL}digital/licenses/archive/request/sign`;
}

export default function ArchiveDigitalLicensePage({}) {
  const [archiveAgreementText, setArchiveAgreementText] = useState(null);
  const [isApproved, setIsApproved] = useState(null);
  const [isSigned, setIsSigned] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showBankId, setShowBankId] = useState(false);
  const [agreementResponse, setAgreementResponse] = useState(null);
  const { dataId, id: archiveDigitalLicenseRequestId } = useParams();
  const api = useApi();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    const response = await api.fetch(
      `${process.env.REACT_APP_MAIN_URL}digital/licenses/archive/request?archiveDigitalLicenseRequestId=${archiveDigitalLicenseRequestId}&dataId=${dataId}`,
      false,
      "GET"
    );
    if (response && response.isSuccessful) {
      setArchiveAgreementText(response.agreementText);
      setIsApproved(response.isApproved);
      setIsSigned(response.isSigned);
      setCompanyName(response.companyName);
    }
    setIsLoading(false);
  }, [archiveDigitalLicenseRequestId, dataId]);

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      await Translate.loadPreLogin(29);
      await loadData(archiveDigitalLicenseRequestId, dataId);
      setIsLoading(false);
    }
    init();
  }, [archiveDigitalLicenseRequestId, dataId]);

  async function onBankIdSignatureSuccess() {
    await loadData(archiveDigitalLicenseRequestId, dataId);
    setShowBankId(false);
  }

  async function onBankIdSignatureFailure(_, response) {
    const translationKey = response?.data?.errorMessageTranslationKey ?? "SomethingFailed";
    const successMessage = enqueueSnackbar(Translate.get(translationKey), {
      variant: "error",
      autoHideDuration: 6000,
      onClick: () => closeSnackbar(successMessage),
    });

    setShowBankId(false);
  }

  function handleAgreementResponse(newResponse) {
    setShowBankId(true);
    setAgreementResponse(newResponse);
  }

  function handleCancelAgreementResponse() {
    setShowBankId(false);
    setAgreementResponse(null);
  }

  return (
    <>
      {isLoading && <Preloader />}
      {showBankId && agreementResponse != null && (
        <Dialog open>
          <DialogTitle></DialogTitle>
          <DialogContent>
            <BankIdAuthentication
              fetchStatusIncludeCredentials={false}
              startAuthenticationUrl={`${process.env.REACT_APP_MAIN_URL}digital/licenses/archive/request/sign`}
              postData={{
                archiveDigitalLicenseRequestId,
                dataId,
                agreementResponse,
              }}
              onAuthenticationSuccess={onBankIdSignatureSuccess}
              onAuthenticationFailure={onBankIdSignatureFailure}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelAgreementResponse}>{Translate.get("Cancel")}</Button>
          </DialogActions>
        </Dialog>
      )}
      <RootStyle
        className={
          "loginPage"
          // window.global.isKs
          //   ? smallScreen
          //     ? "loginPagePermitsMobile"
          //     : "loginPagePermits"
          //   : "loginPage"
        }
      >
        <Container maxWidth="sm">
          <ContentStyle>
            <Box className="archiveDigitalLicenseRequestBox">
              <Typography variant="h5" gutterBottom>
                {Translate.get("ArchiveDigitalLicenseRequestTitle")}
              </Typography>
              <Box
                /* Vet ej om denna ska vara exakt samma styling som hjälpen, men funkar ok */
                className="helpContentBox"
                dangerouslySetInnerHTML={{ __html: archiveAgreementText }}
              ></Box>
              {isSigned && (
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} width="100%">
                  <CheckCircleOutline color="success" sx={{ fontSize: "50px" }} />
                  <Box>
                    {isApproved
                      ? Translate.get("ArchiveDigitalLicenseRequestSignedApproved")
                      : Translate.get("ArchiveDigitalLicenseRequestSignedDeclined")}
                  </Box>
                </Stack>
              )}
              {!isSigned && (
                <>
                  <Box>
                    {Translate.get("ArchiveDigitalLicenseRequestInstructions").replace("{companyName}", companyName)}
                  </Box>
                  <Stack direction="column" alignItems="flex-end" spacing={2}>
                    <Typography
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {Translate.get("BankIdSignRequired")}
                    </Typography>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%" spacing={2}>
                      <Button variant="outlined" onClick={() => handleAgreementResponse(false)}>
                        {Translate.get("Decline")}
                      </Button>
                      <Button variant="contained" onClick={() => handleAgreementResponse(true)}>
                        {Translate.get("ApproveB")}
                      </Button>
                    </Stack>
                  </Stack>
                </>
              )}
            </Box>
          </ContentStyle>
        </Container>
      </RootStyle>
    </>
  );
}
